import { useEffect, useState } from 'react';

const ExistingDeviceChoices = ({ api, kind, fields, selectedId, onSelect, disabled }) => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const plural = `${kind}s`;

    useEffect(() => {
        const controller = new AbortController();
        const fetchRecords = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await api.get('all', { withCredentials: true, signal: controller.signal });
                if (controller.signal.aborted) return;
                const emptyMessage = `[ERROR] No ${kind === 'converter' ? 'Converters' : 'Sensors'} found in database`;
                if (response.data.success || response.data.message === emptyMessage) {
                    setRecords(response.data.payload || []);
                } else {
                    setError(`Could not load existing ${plural}. You can retry or enter the details manually.`);
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    setError(`Could not load existing ${plural}. You can retry or enter the details manually.`);
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        fetchRecords();
        return () => controller.abort();
    }, [api, kind, plural, reload]);

    const selected = records.find((record) => record.id === selectedId);
    const valueOf = (record, key) => String(record[key] ?? '');

    return (
        <section className="device-new-existing" aria-label={`Existing ${plural}`}>
            <p className="bold-label">{kind === 'converter' ? 'Converters' : 'Sensors'} in database:</p>
            <p>Choose existing details to fill in the fields above. You can edit them before adding.</p>
            {loading ? <p role="status">Loading {plural}...</p> : error ? (
                <div role="alert">
                    <p>{error}</p>
                    <button type="button" className="device-new-existing-retry" onClick={() => setReload((previous) => previous + 1)}>Retry</button>
                </div>
            ) : records.length === 0 ? <p>No {plural} in the database yet.</p> : (
                <div className="device-new-existing-choices">
                    {fields.map((field, index) => {
                        const preceding = fields.slice(0, index);
                        const matching = index === 0 ? records : selected ? records.filter((record) => (
                            preceding.every(({ key }) => valueOf(record, key) === valueOf(selected, key))
                        )) : [];
                        const options = [...new Set(matching.map((record) => valueOf(record, field.key)))];
                        const id = `existing-${kind}-${field.key}-select`;
                        return (
                            <div key={field.key} className="device-new-existing-choice">
                                <label htmlFor={id} className="bold-label">{field.label}:</label>
                                <select
                                    id={id}
                                    className="choice-select device-new-existing-select"
                                    value={selected ? valueOf(selected, field.key) : ''}
                                    disabled={disabled || (index > 0 && !selected)}
                                    onChange={(event) => onSelect(matching.find((record) => valueOf(record, field.key) === event.target.value))}
                                >
                                    {(index === 0 || !selected) && <option value="">{field.label}...</option>}
                                    {options.map((value) => <option key={value} value={value}>{value || 'Not specified'}</option>)}
                                </select>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

export default ExistingDeviceChoices;
